/*
 *
 * FormBuilder
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import ConditionalityGraph from 'utils/conditionality.graph';
import injectReducer from 'utils/injectReducer';
import NotificationEngine from 'containers/NotificationEngine';
import Header from 'components/Header/index.tsx';
import FormBuilderView from 'components/FormBuilderView';
import Footer from 'components/Footer';
import DeleteForm from 'components/DeleteForm';
import { handleLoadForm } from 'components/NavBar/utils';
import { filterAnswersByActive, mapAnswersToUnfilteredIndices } from 'utils/branchingUtils';
import makeSelectFormBuilder from './selectors';
import {
  addField,
  addStep,
  bulkToggleDropdownOptions,
  clearForm,
  collapseAllFields,
  deleteField,
  expandAllFields,
  setActiveField,
  removeStep,
  reorderDropdown,
  reorderField,
  setFieldInferredState,
  showFormErrors,
  removeAllUserBranchMembership,
  toggleBranching,
  toggleBranchMembership,
  toggleFieldCollapsedState,
  updateFieldName,
  updateFormValue,
  updateUIState,
  updateDefaultDegreeOffering,
  updateProgramOfStudy,
  toggleThankYouRoute,
  setThankYouRoute,
  refreshThankYouRoute,
  changeConsentStyle,
  updateFormValidity,
} from './actions';
import reducer from './reducer';
import NavBar from '../../components/NavBar';
import EmbedFormDataSection from '../../components/EmbedFormDataSection';
import { receiveCredentialTypes } from '../CredentialTypeList/actions';
import credentialTypeListReducer from '../CredentialTypeList/reducer';
import { getCredentialTypes } from '../../api/taxi.backend';

import { useFetchGroupingDetails } from './hooks/useFetchGroupingDetails';
import { useFetchSfFieldData } from './hooks/useFetchSfFieldData';

export const FormBuilder = (props) => {
  const formRef = React.useRef();
  const formBuilderViewRef = React.useRef();
  const embedFormRef = React.useRef();
  const [credentialTypeDisplay, setCredentialTypeDisplay] = React.useState();

  const { fetchGroupingDetails } = useFetchGroupingDetails();
  const { fetchFieldData } = useFetchSfFieldData();

  React.useEffect(() => {
    let isMounted = true;

    const fetchCredentialTypeAndGroupingData = async () => {
      let credentialTypesData;
      try {
        credentialTypesData = await getCredentialTypes();
      } catch (e) {
        console.log(e); // eslint-disable-line no-console
      }
      props.receiveCredentialTypes(credentialTypesData);
      const currentCredentialType = credentialTypesData.find((credentialType) => credentialType.id === Number(props.match.params.credentialTypeId));

      if (props.match.params && props.match.params.formId) {
        props.loadForm(props.match.params.formId, fetchFieldData);
      } else {
        props.clearForm(currentCredentialType.value);
      }

      if (isMounted) {
        setCredentialTypeDisplay(currentCredentialType.displayName);
      }

      fetchGroupingDetails(props.match.params.groupingId); // Cannot happen before clearForm call
    };

    fetchCredentialTypeAndGroupingData();

    return () => {
      isMounted = false;
    };
  }, []);

  const scrollToBottom = () => {
    formBuilderViewRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  const scrollToTop = () => {
    formRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const snapToTop = () => {
    formRef?.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  };

  const scrollToEmbedForm = () => {
    embedFormRef?.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  };

  const { branchingFields, dehydratedGraph } = props.form.fieldConditionalityInfo;

  const hydratedGraph = new ConditionalityGraph(dehydratedGraph);
  const fieldConditionalityInfo = {
    branchingFields,
    dehydratedGraph,
    graph: hydratedGraph,
  };

  const formData = {
    ...props.form,
    fieldConditionalityInfo,
    id: props.match.params.formId,
    groupingName: props.form.groupingName || '',
  };

  const activeBranches = Object.keys(branchingFields).reduce((memo, key) => {
    const allFieldAnswers = branchingFields[key];
    const activeFieldAnswers = filterAnswersByActive(allFieldAnswers, formData.fields[key]);
    memo[key] = mapAnswersToUnfilteredIndices(activeFieldAnswers, allFieldAnswers); // eslint-disable-line no-param-reassign
    return memo;
  }, {});

  const activeBranchingFieldDetails = Object.keys(formData.fieldConditionalityInfo.branchingFields)
    .map((fieldId) => ({ name: formData.fields[fieldId].name, id: fieldId }))
    .reduce((acc, curr) => {
      const fieldOrderIndex = props.form.fieldOrder.findIndex((el) => el === curr.id);
      let associatedStep = 0;
      for (let i = 0; i < fieldOrderIndex; i++) {
        if (props.form.fieldOrder[i].startsWith('screen-')) {
          associatedStep++;
        }
      }
      acc.push({ ...curr, step: associatedStep });
      return acc;
    }, []);
  const DeleteFormFooter = props.match.params.formId ? (
    <Footer>
      <DeleteForm
        formId={props.form.id || props.match.params.formId}
        formGroupingId={props.form.groupingId || props.match.params.groupingId}
        formName={props.form.name}
      />
    </Footer>
  ) : null;
  return (
    <div ref={formRef}>
      <Header>
        <NotificationEngine />
        <NavBar
          path={[
            { label: credentialTypeDisplay || ' ', link: `/credential-type/${props.match.params.credentialTypeId}/groupings/` },
            { label: props.form.groupingName, link: `/credential-type/${props.match.params.credentialTypeId}/groupings/${props.match.params.groupingId}/forms` },
            { label: formData.name },
          ]}
          activeBranches={activeBranches}
          collapseAllFields={props.collapseAllFields}
          expandAllFields={props.expandAllFields}
          form={formData}
          isFormDirty={props.meta.isDirty}
          loadInProgress={props.meta.loadInProgress}
          onAddField={props.addField}
          onAddStep={props.addStep}
          scrollToBottom={scrollToBottom}
          scrollToEmbedForm={scrollToEmbedForm}
          scrollToTop={scrollToTop}
          showFormErrors={props.showFormErrors}
          snapToTop={snapToTop}
          invalidFormData={props.meta.invalidFormData}
          visibleBranches={props.meta.uiState.visibleBranches}
          updateUIState={props.updateUIState}
        />
      </Header>
      <div className="mb-2 pb-4" ref={formBuilderViewRef}>
        <FormBuilderView
          activeBranches={activeBranches}
          activeBranchingFieldDetails={activeBranchingFieldDetails}
          addField={props.addField}
          addStep={props.addStep}
          availableCredentialTypes={props.meta.availableCredentialTypes}
          availablePrograms={props.meta.availablePrograms}
          availableProgramsOfStudy={props.meta.availableProgramsOfStudy}
          bulkToggleDropdownOptions={props.bulkToggleDropdownOptions}
          changeConsentStyle={props.changeConsentStyle}
          deleteField={props.deleteField}
          deleteStep={props.deleteStep}
          fieldNameDropdownOpts={props.meta.fieldNameDropdownOpts}
          form={formData}
          fieldClicked={props.fieldClicked}
          fieldValidityChanged={props.fieldValidityChanged}
          invalidFormData={props.meta.invalidFormData}
          isDirty={props.meta.isDirty}
          loadInProgress={props.meta.loadInProgress}
          reorderDropdown={props.reorderDropdown}
          reorderField={props.reorderField}
          scrollToBottom={scrollToBottom}
          sfFieldData={props.meta.sfFieldData}
          supportsDegreeOffering={props.meta.supportsDegreeOffering}
          removeAllUserBranchMembership={props.removeAllUserBranchMembership}
          toggleBranching={props.toggleBranching}
          toggleThankYouRoute={props.toggleThankYouRoute}
          setThankYouRoute={props.setThankYouRoute}
          refreshThankYouRoute={props.refreshThankYouRoute}
          toggleBranchMembership={props.toggleBranchMembership}
          toggleFieldCollapsedState={props.toggleFieldCollapsedState}
          setFieldInferredState={props.setFieldInferredState}
          uiState={props.meta.uiState}
          updateDefaultDegreeOffering={props.updateDefaultDegreeOffering}
          updateFieldName={props.updateFieldName}
          updateFormValue={props.updateFormValue}
          updateProgramOfStudy={props.updateProgramOfStudy}
          updateUIState={props.updateUIState}
        />
      </div>
      <div className="pb-5 pt-5" ref={embedFormRef} />
      <div className="bg-white border-top border-bottom pt-3 mb-4">
        <EmbedFormDataSection
          meta={props.meta}
          form={props.form}
          onFieldValueChange={props.updateFormValue}
          onFieldUIStateChange={props.updateUIState}
        />
      </div>
      {DeleteFormFooter}
    </div>
  );
};

FormBuilder.propTypes = {
  addField: PropTypes.func.isRequired,
  addStep: PropTypes.func.isRequired,
  bulkToggleDropdownOptions: PropTypes.func.isRequired,
  changeConsentStyle: PropTypes.func.isRequired,
  clearForm: PropTypes.func.isRequired,
  collapseAllFields: PropTypes.func.isRequired,
  deleteField: PropTypes.func.isRequired,
  deleteStep: PropTypes.func.isRequired,
  expandAllFields: PropTypes.func.isRequired,
  fieldClicked: PropTypes.func.isRequired,
  fieldValidityChanged: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  loadForm: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      credentialTypeId: PropTypes.string.isRequired,
      groupingId: PropTypes.string.isRequired,
      formId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  meta: PropTypes.object.isRequired,
  receiveCredentialTypes: PropTypes.func.isRequired,
  reorderField: PropTypes.func.isRequired,
  reorderDropdown: PropTypes.func.isRequired,
  showFormErrors: PropTypes.func.isRequired,
  removeAllUserBranchMembership: PropTypes.func.isRequired,
  toggleBranching: PropTypes.func.isRequired,
  toggleThankYouRoute: PropTypes.func.isRequired,
  setThankYouRoute: PropTypes.func.isRequired,
  refreshThankYouRoute: PropTypes.func.isRequired,
  toggleBranchMembership: PropTypes.func.isRequired,
  toggleFieldCollapsedState: PropTypes.func.isRequired,
  setFieldInferredState: PropTypes.func.isRequired,
  updateDefaultDegreeOffering: PropTypes.func.isRequired,
  updateProgramOfStudy: PropTypes.func.isRequired,
  updateFieldName: PropTypes.func.isRequired,
  updateFormValue: PropTypes.func.isRequired,
  updateUIState: PropTypes.func.isRequired,
};

const mapStateToProps = makeSelectFormBuilder();

function mapDispatchToProps(dispatch) {
  return {
    addField: (id, index) => dispatch(addField(id, index)),
    addStep: (id) => dispatch(addStep(id)),
    bulkToggleDropdownOptions: (id, dropdownOptions, value) => dispatch(bulkToggleDropdownOptions(id, dropdownOptions, value)),
    changeConsentStyle: (consentStyle) => dispatch(changeConsentStyle(consentStyle)),
    clearForm: (credentialType) => dispatch(clearForm(credentialType)),
    collapseAllFields: () => dispatch(collapseAllFields()),
    deleteField: (keyPath) => dispatch(deleteField(keyPath)),
    deleteStep: (stepIdx) => dispatch(removeStep(stepIdx)),
    expandAllFields: () => dispatch(expandAllFields()),
    fieldClicked: (id) => {
      dispatch(setActiveField(id));
    },
    fieldValidityChanged: (keyPath, isValid, errorText, replaceError = false) => {
      dispatch(updateFormValidity(keyPath, isValid, errorText, replaceError));
    },
    loadForm: (id, fetchFieldData) => handleLoadForm(id, fetchFieldData, dispatch),
    receiveCredentialTypes: (credentialTypes) => dispatch(receiveCredentialTypes(credentialTypes)),
    reorderDropdown: (keyPath, idx, direction) => dispatch(reorderDropdown(keyPath, idx, direction)),
    reorderField: (source, target) => dispatch(reorderField(source, target)),
    showFormErrors: () => dispatch(showFormErrors()),
    removeAllUserBranchMembership: (fieldId) => dispatch(removeAllUserBranchMembership(fieldId)),
    toggleBranching: (fieldId) => dispatch(toggleBranching(fieldId)),
    toggleThankYouRoute: (fieldId) => dispatch(toggleThankYouRoute(fieldId)),
    setThankYouRoute: (fieldId, answer, url) => dispatch(setThankYouRoute(fieldId, answer, url)),
    refreshThankYouRoute: (fieldId) => dispatch(refreshThankYouRoute(fieldId)),
    toggleBranchMembership: (fieldId, answerVal, branchMember) => dispatch(toggleBranchMembership(fieldId, answerVal, branchMember)),
    toggleFieldCollapsedState: (index) => dispatch(toggleFieldCollapsedState(index)),
    setFieldInferredState: (fieldName, value, fieldId) => dispatch(setFieldInferredState(fieldName, value, fieldId)),
    updateDefaultDegreeOffering: (obj) => dispatch(updateDefaultDegreeOffering(obj)),
    updateProgramOfStudy: (obj) => dispatch(updateProgramOfStudy(obj)),
    updateFieldName: (keyPath, incomingFieldName) => dispatch(updateFieldName(keyPath, incomingFieldName)),
    updateFormValue: (keyPath, value) => dispatch(updateFormValue(keyPath, value)),
    updateUIState: async (keyPath, value) => {
      dispatch(updateUIState(keyPath, value));
      dispatch(setActiveField(keyPath[1]));
    },
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withReducer = injectReducer({ key: 'formBuilder', reducer });
const withCredentialTypeReducer = injectReducer({ key: 'credentialTypeList', reducer: credentialTypeListReducer });

export default compose(
  withReducer,
  withCredentialTypeReducer,
  withConnect,
)(FormBuilder);
